import { AtomicStep, StepAnswer } from '../types';

/**
 * Returns exact score color according to user requirements:
 * - 100% -> Green (#10b981)
 * - 70% - 99% -> Orange (#f97316)
 * - Below 70% -> Red (#ef4444)
 */
export const getScoreColor = (score: number): string => {
  if (score === 100) return '#10b981'; // Green for 100%
  if (score >= 70) return '#f97316';  // Orange for 70% - 99%
  return '#ef4444';                    // Red for below 70%
};

/**
 * Returns background color (rgba) for score badges
 */
export const getScoreBgColor = (score: number): string => {
  if (score === 100) return 'rgba(16, 185, 129, 0.18)'; // Green bg
  if (score >= 70) return 'rgba(249, 115, 22, 0.18)';  // Orange bg
  return 'rgba(239, 68, 68, 0.18)';                    // Red bg
};

/**
 * Returns border color (rgba) for score badges
 */
export const getScoreBorderColor = (score: number): string => {
  if (score === 100) return 'rgba(16, 185, 129, 0.4)'; // Green border
  if (score >= 70) return 'rgba(249, 115, 22, 0.4)';  // Orange border
  return 'rgba(239, 68, 68, 0.4)';                    // Red border
};

/**
 * Returns score performance label
 */
export const getScoreLabel = (score: number): string => {
  if (score === 100) return 'Optimal Performance';
  if (score >= 70) return 'Moderate — Room for Improvement';
  return 'Requires Optimization';
};

/**
 * Evaluates whether a step has room for improvement (issue detected)
 * or is 100% optimal (no issue / no strategic advice needed).
 */
export const hasRoomForImprovement = (
  step: AtomicStep,
  answer?: StepAnswer,
  customRec?: string
): boolean => {
  if (customRec !== undefined && customRec.trim() !== '') {
    return true;
  }
  if (!answer || !answer.completed) return false;

  const fv = answer.fieldValues || {};

  if (step.id === 'step-pingdom-speed') {
    const loadTime = parseFloat(String(fv.pingdomLoadTime || '0'));
    return loadTime > 2.0;
  }

  if (step.id === 'step-keyword-focus') {
    return fv.observedKeywordPattern !== 'clear_target';
  }

  if (step.id === 'step-static-vs-dynamic') {
    return fv.contentGrowthClassification !== 'dynamic_regular';
  }

  if (step.id === 'step-indexation-google') {
    return fv.googleIndexMatch !== 'normal_indexation';
  }

  if (step.id === 'step-social-media') {
    return fv.socialPresenceStatus !== 'active_linked';
  }

  if (step.id === 'step-ssl-security') {
    return fv.sslInstalled === false || fv.sslInstalled === 'no' || fv.httpsRedirectActive === false;
  }

  if (step.id === 'step-robotstxt') {
    return fv.robotsTxtValid === false || fv.robotsTxtValid === 'no' || fv.robotsTxtExists === false;
  }

  if (step.id === 'step-sitemap') {
    return fv.sitemapValid === false || fv.sitemapValid === 'no' || fv.sitemapExists === false;
  }

  if (step.id === 'step-siteliner') {
    const dupPct = parseFloat(String(fv.sitelinerDuplicatePct || '0'));
    return dupPct > 15;
  }

  if (step.id === 'step-w3c-validation') {
    const errors = parseInt(String(fv.w3cErrorCount || '0'), 10);
    return errors > 0;
  }

  if (step.id === 'step-moz-authority') {
    const da = parseInt(String(fv.domainAuthority || '0'), 10);
    const spam = parseInt(String(fv.spamScore || '0'), 10);
    return da < 30 || spam > 5;
  }

  if (step.id === 'step-meta-keywords' || step.id === 'step-setup-client' || step.id === 'step-titles-total') {
    return false;
  }

  if (answer.calculatedValues) {
    const pcts = Object.entries(answer.calculatedValues)
      .filter(([k]) => !k.endsWith('_display'))
      .map(([, v]) => Number(v));
    if (pcts.some(p => !isNaN(p) && p > 0)) {
      return true;
    }
  }

  for (const [key, val] of Object.entries(fv)) {
    if (key.toLowerCase().includes('total') || key.toLowerCase().includes('page')) continue;
    const num = Number(val);
    if (!isNaN(num) && num > 0) {
      return true;
    }
  }

  return false;
};

export interface FormattedField {
  id: string;
  label: string;
  value: any;
  formattedValue: string;
}

export interface FormattedUrlList {
  id: string;
  label: string;
  urls: string[];
}

/**
 * Separates step answer fields into clean standard metrics and actual URL/text lists
 * with clean human-readable labels instead of raw technical variable names.
 */
export const processStepFields = (
  step: AtomicStep,
  fieldValues: Record<string, any>,
  formatFieldValueFn: (val: any) => string
): { standardFields: FormattedField[]; urlLists: FormattedUrlList[] } => {
  const standardFields: FormattedField[] = [];
  const urlLists: FormattedUrlList[] = [];

  const fieldLookup: Record<string, any> = {};
  step.fields.forEach(f => {
    fieldLookup[f.id] = f;
  });

  Object.entries(fieldValues).forEach(([fk, fv]) => {
    const fieldDef = fieldLookup[fk];
    const fieldLabel = fieldDef?.label || fk;
    const isTextType = fieldDef?.type === 'text';

    if (fv === undefined || fv === null || (typeof fv === 'string' && fv.trim() === '')) {
      return;
    }

    const strVal = String(fv).trim();
    const containsHttp = strVal.includes('http://') || strVal.includes('https://');
    const containsLineBreaks = strVal.includes('\n');

    // Field is a URL list ONLY if it is a text area containing actual URLs or multi-line text list
    const isExplicitUrlListField = isTextType && (
      containsHttp ||
      containsLineBreaks ||
      fk.toLowerCase().endsWith('urls') ||
      fk.toLowerCase().endsWith('urlslist') ||
      fk.toLowerCase().endsWith('list')
    );

    // Single numeric strings like "0", "269", "1" MUST stay in standardFields!
    const isNumeric = !isNaN(Number(strVal)) && !containsHttp && !containsLineBreaks;

    if (isExplicitUrlListField && !isNumeric) {
      const urls = strVal.split('\n').map(u => u.trim()).filter(u => u.length > 0);
      if (urls.length > 0) {
        urlLists.push({
          id: fk,
          label: fieldLabel,
          urls,
        });
      }
    } else {
      standardFields.push({
        id: fk,
        label: fieldLabel,
        value: fv,
        formattedValue: formatFieldValueFn(fv),
      });
    }
  });

  return { standardFields, urlLists };
};
